import { Box, Typography, Button, Grid, Tooltip, TextField, Alert, IconButton } from '@mui/material';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import { memo, useMemo, useEffect, useCallback, useRef } from 'react';
import { getDayContext, combineDateTime, m } from '../utils/dateUtils';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import VpnKey from '@mui/icons-material/VpnKey';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CustomSelect from './CustomSelect';
import VehicleConflictModal from './VehicleConflictModal';
import { useOverlayStore } from '../stores/useOverlayStore';
import { useVehiclesStore, NO_VEHICLE_ID } from '../stores/useVehiclesStore';
import { useChegadaByCodigoAndDate } from '../hooks/chegadasViagens/useChegadaByCodigoAndDate';
import { useSchedule } from '../hooks/schedules/useSchedule';
import useEnsureTripByCodigoDate from '../hooks/trips/useEnsureTripByCodigoDate';
import useEnsureTripLock from '../hooks/trips/useEnsureTripLock';
import { useLoadingStore } from '../stores/useLoadingStore';
import { usePontos } from '../hooks/pontos/usePontos';
import useEnsureVehicleValidation from '../hooks/vehicles/useEnsureVehicleValidation';
import { useVehicleConflictStore } from '../stores/useVehicleConflictStore';
import { useCalendarModalStore } from '../stores/useCalendarModalStore';
import { useSocketStore } from '../stores/useSocketStore';
import { useDateStore } from '../stores/useDateStore';
import { useToast } from '../hooks/toast/useToast';
import { VEHICLE_CHANGE_MOTIVES, NO_VEHICLE_MOTIVES } from '../utils/tripUtils';
import { usePermissions } from '../hooks/auth/usePermissions';

const getColor = (day, mode) => {
    if (day.reason === 'no_schedule' && mode !== 'single') {
        return 'error';
    }

    if (day.isToday) return 'success';
    if (day.isNext) return 'warning';

    return 'info';
};

// TOOLTIP
const getTooltip = (day, mode) => {
    const {
        currentDayLabel,
        nextDayLabel, 
    } = useDateStore.getState();
    
    if (day.reason === 'single_only') return `DISPONÍVEL APENAS PARA ${currentDayLabel} OU ${nextDayLabel}`;
    if (day.reason === 'today_done') return `JÁ ESCALADO PARA ${currentDayLabel}`;
    if (day.reason === 'next_done') return `JÁ ESCALADO PARA ${nextDayLabel}`;

    if (day.reason === 'no_schedule') {
        return mode === 'single'
            ? 'NÃO OPERA NESTE DIA'
            : 'NÃO PREVISTO OPERAR NESTE DIA (ESCALA MANUAL PERMITIDA)';
    }

    if (day.isToday) return `ESCALAR PARA ${currentDayLabel}`;
    if (day.isNext) return `ESCALAR PARA ${nextDayLabel}`;

    return 'ESCALAR PARA ESSE DIA';
};

const CalendarModal = ({ line, mode = 'full', offset = 0, onClose }) => {

    const { minAllowedDate } = usePermissions();

    const pastDateInputRef = useRef(null);

    const setCanClose = useOverlayStore(state => state.setCanClose);

    //CODIGO
    const codigo = line.CODIGO_INTEGRACAO;

    //NOMELINHA
    const nomeLinha = line.NOMELINHA;

    //ESCALAS HOJE E AMANHA
    const isEscaladaToday = line.isEscaladaToday;
    const isEscaladaNext = line.isEscaladaNext;

    // TÍTULO
    const title = useMemo(() => {
        return mode === 'single'
            ? 'Escalar viagem'
            : 'Selecionar dia para escalar';
    }, [mode]);

    const setLoadingBackdrop = useLoadingStore(state => state.setLoadingBackdrop);
    const setLoadingTxt = useLoadingStore(state => state.setLoadingTxt);

    const veiculosDisponiveis = useVehiclesStore(state => state.veiculosDisponiveis);

    const veiculosComOpcaoSemVeiculo = useVehiclesStore(state => state.veiculosComOpcaoSemVeiculo);

    const selectedVehicle = useCalendarModalStore(state => state.selectedVehicle);
    const setSelectedVehicle = useCalendarModalStore(state => state.setSelectedVehicle);

    const vehicleChangeMotive = useCalendarModalStore(state => state.vehicleChangeMotive);
    const setVehicleChangeMotive = useCalendarModalStore(state => state.setVehicleChangeMotive);

    const selectedDayFlag = useCalendarModalStore(state => state.selectedDayFlag);
    const setSelectedDayFlag = useCalendarModalStore(state => state.setSelectedDayFlag);

    const timeBefore = useCalendarModalStore(state => state.timeBefore);
    const setTimeBefore = useCalendarModalStore(state => state.setTimeBefore);

    const inicioManual = useCalendarModalStore(state => state.inicioManual);
    const setInicioManual = useCalendarModalStore(state => state.setInicioManual);

    const fimManual = useCalendarModalStore(state => state.fimManual);
    const setFimManual = useCalendarModalStore(state => state.setFimManual);

    const openConflictModal = useCalendarModalStore(state => state.openConflictModal);

    const setOpenConflictModal = useCalendarModalStore(state => state.setOpenConflictModal);

    const vehicleChanges = useVehicleConflictStore(
        state => state.vehicleChanges
    );

    const selectedPastDate = useCalendarModalStore(
        state => state.selectedPastDate
    );

    const setSelectedPastDate = useCalendarModalStore(
        state => state.setSelectedPastDate
    );

    const hasVehicleChange = vehicleChanges.size > 0;

    const handleSelectDay = useCallback((
        flag,
        userAction = false,
        pastDate = null
    ) => {

        const state = useCalendarModalStore.getState();

        setSelectedVehicle(state.selectedVehicle ?? '');

        if(userAction){

            setInicioManual(null);
            setFimManual(null);
            setTimeBefore(20);
            useVehicleConflictStore.getState().clearAll();

        } else {

            if (!state.inicioManual) {
                setInicioManual(null);
            }

            if (!state.fimManual) {
                setFimManual(null);
            }

            setTimeBefore(state.timeBefore ?? 20);

        }

        setSelectedPastDate(pastDate);
        setSelectedDayFlag(flag);

    }, [
        setSelectedVehicle,
        setInicioManual,
        setFimManual,
        setTimeBefore,
        setSelectedPastDate,
        setSelectedDayFlag
    ]);

    // DIAS
    const days = useMemo(() => {
        const { selectedDate } = useDateStore.getState();
        return Array.from({ length: 7 }).map((_, i) => {
            const ctx = getDayContext(selectedDate, i);
            const hasSchedule = line?.[ctx.flag] === 'S';

            const isToday = i === 0;
            const isNext = i === 1;

            let reason = null;

            //regra do mode SINGLE
            if (mode === 'single' && !isToday && !isNext) {
                reason = 'single_only';
            }
            else if (!hasSchedule) {
                reason = 'no_schedule';
            }
            else if (isToday && isEscaladaToday) {
                reason = 'today_done';
            }
            else if (isNext && isEscaladaNext) {
                reason = 'next_done';
            }

            const isAllowedInFull =
                mode !== 'single' &&
                ['no_schedule', 'today_done', 'next_done'].includes(reason);

            const blocked = reason && !isAllowedInFull;

            return {
                ...ctx,
                label: ctx.date.format('DD/MM'),
                dayName: ctx.nome.slice(0, 3),
                hasSchedule,
                blocked,
                reason,
                isToday,
                isNext
            };
        });
    }, [isEscaladaToday, isEscaladaNext, line, mode]);

    const pastDay = useMemo(() => {

        if (!selectedPastDate) {
            return null;
        }

        const ctx = getDayContext(
            selectedPastDate
        );

        const hasSchedule =
            line?.[ctx.flag] === 'S';

        return {
            ...ctx,

            label: ctx.date.format('DD/MM'),

            dayName: ctx.nome.slice(0, 3),

            hasSchedule,

            blocked: false,

            reason: !hasSchedule
                ? 'no_schedule'
                : null,

            isToday: false,

            isNext: false
        };

    }, [
        selectedPastDate,
        line
    ]);

    // AUTO SELEÇÃO no modo single
    useEffect(() => {
        if (mode !== 'single') return;
        const { selectedDate } = useDateStore.getState();
        const ctx = getDayContext(line?.DATA_REFERENCIA ?? selectedDate ?? null, offset);
        handleSelectDay(ctx.flag);
    }, [mode, offset, line?.DATA_REFERENCIA, handleSelectDay]);

    // DIA SELECIONADO
    const selectedDay = useMemo(() => {

        if (mode === 'single') {

            const { selectedDate } = useDateStore.getState();

            const date =
                line?.DATA_REFERENCIA ??
                selectedDate;

            if (!date) {
                return null;
            }

            const ctx = getDayContext(date, offset);

            const hasSchedule = line?.[ctx.flag] === 'S';

            return {
                ...ctx,
                label: ctx.date.format('DD/MM'),
                dayName: ctx.nome.slice(0, 3),
                hasSchedule,
                blocked: false,
                reason: !hasSchedule
                    ? 'no_schedule'
                    : null,
                isToday: offset === 0,
                isNext: offset === 1
            };
        }

        if (
            selectedPastDate &&
            pastDay &&
            pastDay.flag === selectedDayFlag
        ) {
            return pastDay;
        }

        return days.find(
            day => day.flag === selectedDayFlag
        ) || null;

    }, [
        mode,
        line,
        offset,
        selectedDayFlag,
        selectedPastDate,
        pastDay,
        days
    ]);

    const schedule = useSchedule(codigo, selectedDay?.flag);

    const selectedDate = selectedDay?.date?.format('YYYY-MM-DD');
    const rootTripId = selectedDate ? `${selectedDate}-${codigo}` : null;

    const pontos = usePontos(codigo);

    const chegada = useChegadaByCodigoAndDate(codigo, selectedDate);

    const tripToday = line?.TRIP_UID ?? false;

    const isExistingTrip =
        !!line?.VIAGEM_ID &&
        !!line?.DATA_REFERENCIA;

    const shouldLookupTrip =
        mode !== 'single' ||
        !isExistingTrip;
        
    const {
        trip: tripByDate,
        status: tripStatus
    } = useEnsureTripByCodigoDate({
        enabled: shouldLookupTrip,
        tripToday,
        codigo,
        selectedDay
    });

    const trip = isExistingTrip
        ? line
        : tripByDate;

    const tripLookupResolved =
        !shouldLookupTrip ||
        tripStatus === 'done' ||
        tripStatus === 'not_found';

    const lockKey =
        tripLookupResolved
            ? (trip?.TRIP_UID ?? rootTripId)
            : null;

    const {
        blocked,
        blockedTxt,
        hasLock
    } = useEnsureTripLock({
        tripId: trip?.VIAGEM_ID,
        lockKey
    });

    const hasSelectedDay = !!selectedDay && !blocked && hasLock;

    useEffect(() => {

        if (!selectedDay) return;
        if (!trip) return;

        const currentState = useCalendarModalStore.getState();

        if (currentState.restoring) {

            setSelectedVehicle(
                currentState.selectedVehicle ?? trip.license_plate ?? ''
            );

            if (currentState.timeBefore !== 0) {

                setTimeBefore(currentState.timeBefore);

            } else if (
                trip.time_caught &&
                trip.DATAHORA_INICIAL_PREVISTO
            ) {

                const inicio = m(trip.DATAHORA_INICIAL_PREVISTO);
                const caught = m(trip.time_caught);

                const diff = inicio.diff(caught, 'minutes');

                if (diff >= 20) {
                    setTimeBefore(diff);
                }
            }

        } else {

            setSelectedVehicle(trip.license_plate ?? '');

            if (
                trip.time_caught &&
                trip.DATAHORA_INICIAL_PREVISTO
            ) {

                const inicio = m(trip.DATAHORA_INICIAL_PREVISTO);
                const caught = m(trip.time_caught);

                const diff = inicio.diff(caught, 'minutes');

                if (diff >= 20) {
                    setTimeBefore(diff);
                }
            }
        }

    }, [
        selectedDay,
        trip,
        setSelectedVehicle,
        setTimeBefore
    ]);

    const isNoVehicleSelected = selectedVehicle === NO_VEHICLE_ID;

    const safeSelectedVehicle = useMemo(() => {
        if (isNoVehicleSelected) {
            return '';
        }
        const exists = veiculosDisponiveis?.some(
            v => v.id === selectedVehicle
        );
        return exists ? selectedVehicle : '';
    }, [selectedVehicle, isNoVehicleSelected, veiculosDisponiveis]);


    const warningVehicle = useMemo(() => {

        if (
            !selectedVehicle ||
            isNoVehicleSelected
        ) {
            return false;
        }

        return !safeSelectedVehicle
            ? selectedVehicle
            : false;

    }, [
        selectedVehicle,
        safeSelectedVehicle,
        isNoVehicleSelected
    ]);

    //PAYLOAD
    const selectedPayload = useMemo(() => {
        if (!selectedDay) return null;

        const inicioOriginal =
            trip?.DATAHORA_INICIAL_PREVISTO
                ? m(trip.DATAHORA_INICIAL_PREVISTO)
                : (
                    schedule
                        ? combineDateTime(
                            selectedDay.date,
                            schedule.hour_start
                        )
                        : null
                );

        let inicio = inicioManual || inicioOriginal;

        // fallback single
        if (!inicio && mode === 'single' && selectedDay) {
            const agora = m();

            inicio = selectedDay.date
                .clone()
                .hour(agora.hour())
                .minute(agora.minute())
                .second(0);
        }

        // evita passado só no fallback
        const isFallbackInicio =
            mode === 'single' &&
            !inicioManual &&
            !inicioOriginal;

        if (isFallbackInicio && selectedDay?.isToday && inicio) {
            const agora = m();

            if (inicio.isBefore(agora)) {
                inicio = agora.clone().add(1, 'minute');
            }
        }

        //proteção geral
        if (!inicio) return null;

        const mudouInicio =
            inicioOriginal &&
            inicio &&
            !inicio.isSame(inicioOriginal);

        //REGRA FINAL
        let fim = null;

        // 1. PRIORIDADE: manual
        if (fimManual) {
            fim = fimManual;
        }

        // 2. NÃO mudou início - pode usar schedule ou chegada
        else if (!mudouInicio) {

            if (chegada?.DATAHORA_FINAL_PREVISTO) {
                fim = combineDateTime(
                    selectedDay.date,
                    chegada.DATAHORA_FINAL_PREVISTO
                );
            }

            else if (schedule?.hour_end) {
                fim = combineDateTime(
                    selectedDay.date,
                    schedule.hour_end
                );
            }
        }

        // 3. MUDOU início - usar somente pontos
        if (!fim && pontos?.length) {
            const ultimo = pontos[pontos.length - 1];
            fim = inicio.clone().add(ultimo.TEMPO_CHEGADA, 'minutes');
        }

        const safeTimeBefore = Math.max(timeBefore, 20);

        const timeCaught = inicio
            .clone()
            .subtract(safeTimeBefore, 'minutes');

        return {
            CODIGO_INTEGRACAO: codigo,
            DATAHORA_INICIAL_PREVISTO: inicio.format('YYYY-MM-DD HH:mm:ss'),
            DATAHORA_FINAL_PREVISTO: fim?.format('YYYY-MM-DD HH:mm:ss'),
            time_caught: timeCaught.format('YYYY-MM-DD HH:mm:ss'),
            license_plate: selectedVehicle
        };

    }, [
        selectedDay,
        codigo,
        schedule,
        inicioManual,
        fimManual,
        pontos,
        chegada,
        timeBefore,
        selectedVehicle,
        mode,
        trip
    ]);

    const hasFim = !!(selectedPayload?.DATAHORA_FINAL_PREVISTO || fimManual);

    const isFimManual = !!fimManual;

    const isFimAutomatico =
        !!selectedPayload?.DATAHORA_FINAL_PREVISTO && !fimManual;


    const hasInicio = !!selectedPayload?.DATAHORA_INICIAL_PREVISTO;
    const showWarning = hasInicio && !isFimAutomatico;

    const handleOpenConflictModal = useCallback(() => {
        setOpenConflictModal(true);
    }, [setOpenConflictModal]);

    const originalVehicle = trip?.license_plate || null;

    const isRemovingVehicle =
        !!trip &&
        originalVehicle !== NO_VEHICLE_ID &&
        isNoVehicleSelected;

    const isCreatingWithoutVehicle =
        !trip &&
        isNoVehicleSelected;

    const canRestoreOriginalVehicle =
        !!trip &&
        !!originalVehicle &&
        selectedVehicle !== originalVehicle;

    const vehicleMotiveItems = isNoVehicleSelected
        ? NO_VEHICLE_MOTIVES
        : VEHICLE_CHANGE_MOTIVES;

    const needsNoVehicleMotive =
        isCreatingWithoutVehicle ||
        isRemovingVehicle;

    const needsVehicleMotive =
        hasVehicleChange ||
        needsNoVehicleMotive;

    const hasVehicleChangeWithoutMotive =
        needsVehicleMotive &&
        vehicleChangeMotive === 0;

    const {
        loadingVehicle,
        vehicleConflicts,
        hasConflictVehicle,
        setVehicleConflicts
    } = useEnsureVehicleValidation({
        tripId: trip?.VIAGEM_ID ?? rootTripId ?? null,
        codigoIntegracao: selectedPayload?.CODIGO_INTEGRACAO ?? null,
        timeCaught: selectedPayload?.time_caught ?? null,
        vehicle: selectedVehicle,
        startAt: selectedPayload?.DATAHORA_INICIAL_PREVISTO ?? null,
        endAt: selectedPayload?.DATAHORA_FINAL_PREVISTO ?? null
    });

    const loadingConflicts = useVehicleConflictStore(state => state.loadingConflicts);

    const handleCloseConflictModal = useCallback(() => {

        if (loadingConflicts) return;

        setOpenConflictModal(false);

    }, [loadingConflicts, setOpenConflictModal]);

    useEffect(() => {

        if (!codigo) return;
        if (!rootTripId) return;

        if (vehicleConflicts === null) return;

        if (loadingVehicle) return;

        if (!safeSelectedVehicle) return;

        if (
            !selectedPayload?.DATAHORA_INICIAL_PREVISTO ||
            !selectedPayload?.DATAHORA_FINAL_PREVISTO ||
            !selectedPayload?.time_caught
        ) {
            return;
        }

        const store = useVehicleConflictStore.getState();

        store.setVehicleChange(
            rootTripId,
            originalVehicle,
            safeSelectedVehicle
        );

        const calendarStore = useCalendarModalStore.getState();

        if(!calendarStore.restoring) {
            if (!hasConflictVehicle) {

                store.clearTree();

            } else {
                store.createRoot({
                    tripId: rootTripId,
                    parentTripId: null,
                    codigoIntegracao: codigo,
                    originalVehicle,
                    newVehicle: safeSelectedVehicle,
                    startAt: selectedPayload.DATAHORA_INICIAL_PREVISTO,
                    endAt: selectedPayload.DATAHORA_FINAL_PREVISTO,
                    timeCaught: selectedPayload.time_caught
                });

                store.addChildren(
                    rootTripId,
                    vehicleConflicts
                );
            }
        } else {
            calendarStore.setRestoring(false);
        }

        setVehicleConflicts(null);


    }, [
        codigo,
        selectedPayload?.DATAHORA_INICIAL_PREVISTO,
        selectedPayload?.DATAHORA_FINAL_PREVISTO,
        selectedPayload?.time_caught,
        rootTripId,
        loadingVehicle,
        originalVehicle,
        safeSelectedVehicle,
        hasConflictVehicle,
        vehicleConflicts,
        setVehicleConflicts
    ]);

    const hasBlockedConflicts = useVehicleConflictStore(
        state => state.hasBlockedConflicts()
    );

    const allResolved = useVehicleConflictStore(state =>
        state.allResolved()
    );

    const tooltipVVTxt =
        !safeSelectedVehicle
            ? 'Escolha um veículo para verificar conflitos'
            : hasConflictVehicle
                ? (
                    allResolved
                        ? '⚠️ Existem conflitos resolvidos de horário'
                        : '⚠️ Existem conflitos de horário'
                )
                : 'Nenhum conflito encontrado';

    const handleCloseModal = () => {
        onClose?.();
    };

    useEffect(() => {
        if (!rootTripId) return;

        const currentState = useCalendarModalStore.getState();
        if(currentState.restoring){
            return;
        }
        useVehicleConflictStore
            .getState()
            .clearAll();

    }, [rootTripId]);

    const socket = useSocketStore(state => state.socket);

    const { success, warning, error } = useToast();

    const handleConfirm = () => {

        const vehicleChangesMap = useVehicleConflictStore
            .getState()
            .vehicleChanges;

        const vehicleChanges = Array.from(vehicleChangesMap.values());

        if (!allResolved || !selectedPayload) {
            return;
        }

        setLoadingBackdrop(true);
        setLoadingTxt('Salvando...');

        socket.emit(
            'trip:save',
            {
                requestUid: crypto.randomUUID(),
                lockKey,
                trip: selectedPayload,
                vehicleChanges,
                vehicleChangeMotive
            },
            (response) => {
                setLoadingBackdrop(false);
                setLoadingTxt('');

                ({
                    success,
                    warning,
                    error
                }[response.toast])?.(
                    response.message || 'Falha ao salvar as alterações.'
                );

                if (response?.ok) {
                    handleCloseModal();
                }

                
            }
        );
    };

    useEffect(() => {
        setCanClose(!loadingConflicts);
    }, [
        setCanClose,
        loadingConflicts
    ]);

    const hasConflictTree = useVehicleConflictStore(
        state => state.hasConflictTree()
    );

    return (
            <Box
                sx={{
                    width: 760,
                    maxWidth: '90vw'
                }}
            >
            <Typography variant="h6" mb={0}>
                {title}: {codigo}
            </Typography>
            <Typography variant="h6" mb={2}>
                {nomeLinha}
            </Typography>

            {/* DIAS (só mostra no FULL) */}
            {mode === 'full' && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >

                    {/* DATA ANTERIOR */}
                    <Box sx={{ position: 'relative' }}>

                        <Tooltip
                            title={
                                selectedPastDate
                                    ? (
                                        trip
                                            ? `JÁ ESCALADO PARA ${pastDay?.date.format('DD/MM/YYYY')}`
                                            : 'Selecionar outra data anterior'
                                    )
                                    : 'Selecionar uma data anterior'
                            }
                            arrow
                        >
                            <Button
                                variant={selectedPastDate ? 'contained' : 'outlined'}
                                color="secondary"
                                onClick={() => {
                                    pastDateInputRef.current?.showPicker();
                                }}
                                sx={{
                                    height: 70,
                                    minWidth: 72,
                                    flexDirection: 'column',
                                    gap: 0.5
                                }}
                            >
                                <Typography variant="caption">
                                    {pastDay
                                        ? pastDay.dayName
                                        : 'ANT.'
                                    }
                                </Typography>

                                {pastDay ? (
                                    <Typography fontWeight="bold">
                                        {pastDay.label}
                                    </Typography>
                                ) : (
                                    <CalendarMonthIcon fontSize="small" />
                                )}
                            </Button>
                        </Tooltip>

                        <input
                            ref={pastDateInputRef}
                            type="date"

                            value={selectedPastDate ?? ''}

                            min={minAllowedDate}

                            max={
                                m(useDateStore.getState().selectedDate)
                                    .clone()
                                    .subtract(1, 'day')
                                    .format('YYYY-MM-DD')
                            }

                            onChange={(e) => {

                                const value = e.target.value;

                                if (!value) {
                                    setSelectedPastDate(null);
                                    setSelectedDayFlag(null);
                                    return;
                                }

                                const ctx = getDayContext(value);

                                handleSelectDay(
                                    ctx.flag,
                                    true,
                                    value
                                );
                            }}

                            style={{
                                position: 'absolute',
                                width: 1,
                                height: 1,
                                opacity: 0,
                                pointerEvents: 'none'
                            }}
                        />

                    </Box>

                    {/* 7 DIAS ATUAIS */}
                    <Grid
                        container
                        spacing={1}
                        flex={1}
                        justifyContent="space-around"
                    >
                        {days.map((day) => (
                            <Grid key={day.flag}>
                                <Tooltip
                                    title={getTooltip(day, mode)}
                                    arrow
                                >
                                    <span>
                                        <Button
                                            fullWidth
                                            variant={
                                                !selectedPastDate &&
                                                selectedDayFlag === day.flag
                                                    ? 'contained'
                                                    : 'outlined'
                                            }
                                            color={getColor(day, mode)}
                                            disabled={day.blocked}
                                            onClick={() =>
                                                handleSelectDay(
                                                    day.flag,
                                                    true
                                                )
                                            }
                                            sx={{
                                                height: 70,
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                opacity:
                                                    day.blocked
                                                        ? 0.5
                                                        : (
                                                            day.reason === 'no_schedule'
                                                            && mode !== 'single'
                                                                ? 0.9
                                                                : 1
                                                        )
                                            }}
                                        >
                                            <Typography variant="caption">
                                                {day.dayName}
                                            </Typography>

                                            <Typography fontWeight="bold">
                                                {day.label}
                                            </Typography>
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Grid>
                        ))}
                    </Grid>

                </Box>
            )}

            {/* HORÁRIOS e BLOCKED */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} mt={3} mb={1}>
                <Alert severity="info">
                    Horários previstos ({selectedDay?.label || ''})
                </Alert>
                {blocked && (
                    <Alert severity="warning">
                        {blockedTxt || 'Outro usuário já está editando.'}
                    </Alert>
                )}
            </Box>

            <Grid container spacing={1}>
                <Grid>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>

                        {/* INÍCIO */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Início Previsto">
                                <AlarmOnIcon sx={{ fontSize: 20, color: 'success.main' }} />
                            </Tooltip>

                            <Typography sx={{ ml: -1.5, fontWeight: 'bold', color: 'success.main' }}>
                                P
                            </Typography>

                            <Typography>
                                {selectedPayload?.DATAHORA_INICIAL_PREVISTO
                                    ? selectedPayload.DATAHORA_INICIAL_PREVISTO.slice(11, 16)
                                    : '--:--'}
                            </Typography>
                        </Box>

                        {/* FINAL */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Final Previsto">
                                <AlarmOffIcon sx={{ fontSize: 20, color: 'error.main' }} />
                            </Tooltip>

                            <Typography sx={{ ml: -1.5, fontWeight: 'bold', color: 'error.main' }}>
                                P
                            </Typography>

                            <Typography>
                                {selectedPayload?.DATAHORA_FINAL_PREVISTO
                                    ? selectedPayload.DATAHORA_FINAL_PREVISTO.slice(11, 16)
                                    : '--:--'}
                            </Typography>
                        </Box>

                        {/* HORA DE PEGA */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Hora Pegada">
                                <VpnKey sx={{ fontSize: 26, color: 'warning.main' }} />
                            </Tooltip>
                            <Typography>
                                {selectedPayload?.time_caught
                                    ? selectedPayload.time_caught.slice(11, 16)
                                    : '--:--'}
                            </Typography>
                        </Box>
                        {warningVehicle &&
                            <Alert severity="error" variant='filled'>
                                Veículo: {warningVehicle} não encontrado ou inválido.
                            </Alert>
                        }
                    </Box>
                </Grid>
            </Grid>

            {/* INICIO, VEICULO E TEMPO PEGA */}
            <Box
                mt={3}
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    width: '100%'
                }}
            >
                {/* VEÍCULO E MOTORISTA - 60% */}
                <Box
                    sx={(theme) => ({
                        width: '60%',
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1,
                        px: 2,
                        pt: 2,
                        pb: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        backgroundColor: theme.palette.background.paper,
                        position: 'relative'
                    })}
                >
                    {/* LABEL FLOUTING */}
                    <Typography
                        variant="caption"
                        sx={(theme) => ({
                            position: 'absolute',
                            top: -10,
                            left: 10,
                            px: 0.5,
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.secondary,
                            fontWeight: 500
                        })}
                    >
                        Veículo
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                        <Tooltip title="Veículo">
                            <DirectionsBusIcon sx={{ fontSize: 20 }} />
                        </Tooltip>

                        <Box width={'100%'}>
                            <CustomSelect
                                items={veiculosComOpcaoSemVeiculo}
                                selected={selectedVehicle}
                                onChange={(val) => {

                                    const wasNoVehicle =
                                        selectedVehicle === NO_VEHICLE_ID;

                                    const willBeNoVehicle =
                                        val === NO_VEHICLE_ID;

                                    if (wasNoVehicle !== willBeNoVehicle) {
                                        setVehicleChangeMotive(0);
                                    }

                                    setSelectedVehicle(val);
                                }}

                                label="Veículo"
                                allowNone={false}
                                enableSearch={true}
                                layout="minor"
                                scrollToSelected={true}
                                disabled={!hasSelectedDay}
                                margin={1}
                            />
                        </Box>
                        <Tooltip title="Voltar ao Veículo Original">
                            <span>
                                <IconButton
                                    size="small"
                                    color="info"
                                    variant="contained"
                                    disabled={!canRestoreOriginalVehicle}

                                    onClick={() => {
                                        setSelectedVehicle(originalVehicle);
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    {isRemovingVehicle && (
                        <Alert severity="warning">
                            Esta viagem possui o veículo <strong>{originalVehicle}</strong>.
                            Ao confirmar, o veículo será removido da viagem.
                        </Alert>
                    )}

                    {isCreatingWithoutVehicle && (
                        <Alert severity="warning">
                            Esta viagem será criada sem veículo.
                        </Alert>
                    )}

                    <CustomSelect
                        items={vehicleMotiveItems}
                        selected={vehicleChangeMotive}
                        onChange={(val) => setVehicleChangeMotive(val)}
                        label={
                            isNoVehicleSelected
                                ? 'Motivo Sem Veículo'
                                : 'Motivo da Troca'
                        }
                        allowNone={false}
                        enableSearch={true}
                        scrollToSelected={true}
                        disabled={!needsVehicleMotive}
                        layout="minor"
                        margin={0}
                    />

                    <Tooltip title={tooltipVVTxt} arrow>
                        <span style={{ width: '100%' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color={
                                    loadingVehicle
                                        ? 'warning'
                                        : allResolved
                                            ? 'info'
                                            : 'error'
                                }
                                onClick={handleOpenConflictModal}
                                disabled={
                                    loadingVehicle ||
                                    !hasConflictVehicle
                                }
                            >
                                {
                                    loadingVehicle
                                        ? 'Verificando...'
                                        : hasConflictVehicle
                                            ? (
                                                !allResolved
                                                    ? 'Resolver conflitos'
                                                    : 'Conflitos resolvidos'
                                            )
                                            : 'Sem conflitos'
                                }
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                {/* INÍCIO E FIM - 24% */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 1,
                        width: '24%'
                    }}
                >

                    {/* COLUNA INPUTS */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            flex: 1
                        }}
                    >
                        {/* INÍCIO */}
                        <Box
                            sx={{
                                display: 'flex'
                            }}
                        >
                            <TextField
                                label="Início da viagem"
                                type="time"
                                fullWidth
                                disabled={!hasSelectedDay}
                                sx={(theme) => ({
                                    input: {
                                        color: theme.palette.text.primary,
                                    },
                                    '& input::-webkit-calendar-picker-indicator': {
                                        filter: theme.palette.mode === 'dark'
                                            ? 'invert(1)'
                                            : 'invert(0)',
                                        cursor: 'pointer',
                                    }
                                })}
                                value={
                                    inicioManual
                                        ? inicioManual.format('HH:mm')
                                        : selectedPayload?.DATAHORA_INICIAL_PREVISTO
                                            ? selectedPayload.DATAHORA_INICIAL_PREVISTO.slice(11, 16)
                                            : ''
                                }
                                onChange={(e) => {
                                    if (!selectedDay) return;
                                    const value = e.target.value;

                                    if (!value) {
                                        setInicioManual(null);
                                        return;
                                    }

                                    const [h, m] = value.split(':');

                                    const newInicio = combineDateTime(
                                        selectedDay.date,
                                        `${h}:${m}:00`
                                    );

                                    setInicioManual(newInicio);
                                }}
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                            />
                            {/* BOTÃO RESET CENTRALIZADO */}
                            <Tooltip title="Resetar Início">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="info"
                                        variant="contained"

                                        onClick={() => {
                                            setInicioManual(null)
                                        }}
                                    >
                                        <RefreshIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        {/* FINAL (sempre visível) */}
                        <Tooltip
                            title={
                                isFimAutomatico
                                    ? 'Calculado automaticamente'
                                    : isFimManual
                                        ? 'Definido manualmente'
                                        : 'Preencha apenas se o sistema não conseguir calcular automaticamente'
                            }
                            arrow
                        >
                            <span style={{ width: '100%', marginTop: 4 }}>
                                <TextField
                                    label="Final da viagem"
                                    type="time"
                                    fullWidth
                                    color={showWarning ? 'warning' : 'primary'}
                                    disabled={
                                        !hasSelectedDay ||
                                        !selectedPayload?.DATAHORA_INICIAL_PREVISTO ||
                                        isFimAutomatico
                                    }
                                    value={
                                        selectedPayload?.DATAHORA_FINAL_PREVISTO
                                            ? selectedPayload.DATAHORA_FINAL_PREVISTO.slice(11, 16)
                                            : ''
                                    }
                                    onChange={(e) => {
                                        if (!selectedDay) return;
                                        const value = e.target.value;

                                        if (!value) {
                                            setFimManual(null)
                                            return;
                                        }

                                        const [h, m] = value.split(':');

                                        const newFim = combineDateTime(
                                            selectedDay.date,
                                            `${h}:${m}:00`
                                        );

                                        setFimManual(newFim)
                                    }}
                                    sx={(theme) => ({
                                        ...(showWarning && {
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.warning.dark,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.warning.main,
                                            },
                                        }),
                                    })}
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </span>
                        </Tooltip>
                    </Box>

                </Box>
                {/* TEMPO PEGA - FIXO */}
                <TextField
                    sx={{ width: 100 }}
                    label="Pega (min)"
                    type="number"
                    value={timeBefore}
                    disabled={!hasSelectedDay}
                    onChange={(e) => {
                        const val = Number(e.target.value);
                        setTimeBefore(val < 20 ? 20 : val);
                    }}
                    inputProps={{ min: 20 }}
                />
            </Box>

            {/* AÇÕES */}
            <Box mt={3} display="flex" justifyContent="space-between">
                <Button disabled={loadingVehicle} variant="contained" color='error' onClick={handleCloseModal}>Cancelar</Button>

                <Button
                    variant="contained"
                    color='success'
                    disabled={
                        !selectedPayload ||
                        !selectedVehicle ||
                        !hasFim ||
                        blocked ||
                        loadingVehicle ||
                        !allResolved ||
                        hasBlockedConflicts ||
                        hasVehicleChangeWithoutMotive
                    }
                    onClick={handleConfirm}
                >
                    Confirmar
                </Button>
            </Box>

            {hasConflictTree && (
                <VehicleConflictModal
                    open={openConflictModal}
                    onClose={handleCloseConflictModal}
                />
            )}

        </Box>
    );
};

export default memo(CalendarModal);